import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { ServicePathProviderService } from '@blk/cwp-ng-framework';
import { ConfigVariants } from '@ddt-web/config';
import { WebUserDataConstants } from '@ddt-web/domain';
import { InjectSessionStorage, ObjectUtil, SessionStorageKey } from '@ddt-web/generic-utils';
import { UrlUtils } from '@ddt-web/url';
import { Store } from '@ngrx/store';
import * as cloneDeep from 'lodash/cloneDeep';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import * as isString from 'lodash/isString';
import * as lowerCase from 'lodash/lowerCase';
import * as omit from 'lodash/omit';
import * as set from 'lodash/set';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

import { APPLICATION_CONFIG_PATH_TOKEN } from '../constants/application-config.token';
import { contentConfigFetched } from '../constants/config-fetched.actions';
import { ConfigKey } from '../constants/config-key.constants';
import { LocaleUtilResolverService } from '../locale-service';
// TODO chmalcol: Look into making this more generic or extending it to suit other purposes like portfolio tags
@Injectable({
	providedIn: 'root'
})
export class ConfigService {
	public static instance: ConfigService; // this is mainly for the decorator

	@InjectSessionStorage(SessionStorageKey.Config) public sessionConfig: any;

	public configOverrideMode: string;

	private mappings = {};
	private mappings$: Subject<{ [key: string]: any }> = new BehaviorSubject(null);
	private readonly DEFAULT_COUNTRY = 'us';

	constructor(
		@Optional() private store?: Store,
		@Optional() private servicePathProvider?: ServicePathProviderService,
		@Optional() private http?: HttpClient,
		@Optional() private localeUtilResolverService?: LocaleUtilResolverService,
		@Optional() @Inject(APPLICATION_CONFIG_PATH_TOKEN) private applicationConfigPath: string = '',
		@Optional() @Inject('environment') private environment: any = {}
	) {
		ConfigService.instance = this;
		this.setMappings();
	}

	public init(appVariants: (string | ConfigVariants)[]): Promise<{ [key: string]: string }> {
		return this.fetchContentConfig(appVariants)
			.toPromise()
			.then(mappings => {
				this.initialiseConfigFromObj(mappings);
				this.mappings$.next(mappings);
				this.store.dispatch(contentConfigFetched({ contentConfig: mappings }));
				return mappings;
			});
	}

	/**
	 * The hasBeenInitialised function checks whether the config we need has been loaded yet.
	 * It should be used in places where we have no guarantee that the API call would have been completed such as RouteGuards and constructors.
	 * @returns Observable of true if the mappings have been retrieved from the API, of false otherwise - continuing to emit as the mappings are updated
	 */
	public hasBeenInitialised(): Observable<boolean> {
		return this.mappings$.asObservable().pipe(map(value => !!value));
	}

	/**
	 * @returns observable of the mappings that have been retrieved from the API
	 */
	public getDefaultConfigObject(): Observable<any> {
		return this.mappings$.asObservable().pipe(
			map(value => {
				if (value) {
					return { ...this.mappings, ...omit(value, ['@type']) };
				}
			})
		);
	}

	public get<T>(key: ConfigKey, defaultValue: T): T {
		let value = get(this.mappings, key);

		if (this.configOverrideMode && this.mappings['_configOverrideMode']?.[this.configOverrideMode]) {
			value = get(this.mappings, `_configOverrideMode.${this.configOverrideMode}.${key}`, value);
		}

		// transform string boolean values to boolean values - temp to support both version until cleanup is completed
		if (isString(value) && (lowerCase(value) === 'true' || lowerCase(value) === 'false')) {
			value = this.getBoolean(key, defaultValue as unknown as boolean);
		}
		return value ?? defaultValue;
	}

	/** @deprecated use configService.get() instead */
	public getBoolean(key: ConfigKey, defaultValue: boolean): boolean {
		let value = get(this.mappings, key);
		if (value) {
			value = value.toLowerCase();
			if (value === 'true' || value === 'false') {
				return value === 'true';
			} else {
				console.warn('The value for key %s exists but is not a boolean. Returning defaultValue: %s', key, defaultValue);
				return defaultValue;
			}
		} else {
			console.warn('No value exists for key %s. Returning defaultValue: %s', key, defaultValue);
			return defaultValue;
		}
	}

	public fetchContentConfig(variants: string[]): Observable<{ [key: string]: any }> {
		if (!isEmpty(this.applicationConfigPath)) {
			const toolsPath = WebUserDataConstants.CONFIGS + this.applicationConfigPath + WebUserDataConstants.CONFIG_LAYER_CONFIG;
			return this.http.get(this.servicePathProvider.getServicePath('api-gateway') + toolsPath, { params: { variants: variants.join(',') } }).pipe(
				take(1),
				map(jsonResponse => {
					const configVariants: Array<{ [key: string]: string }> = get(jsonResponse, 'resultMap.REPORT', [])
						.map(variantResponse => get(variantResponse, 'data', {}))
						.reverse(); // variants come back in reversed priority from the server so we need to flip the order before flattening
					let configData: { [key: string]: any } = {};
					configVariants.forEach(config => {
						configData = ObjectUtil.extend(configData, config);
					});
					return configData;
				}),
				catchError(error => {
					console.error('Content tags error:', error);
					return of({});
				})
			);
		} else {
			return of({});
		}
	}

	public initialiseConfigFromObj(mappings: any): void {
		// Override via query params
		this.overrideConfigFromQueryParam(mappings);

		const config = this.sessionConfig ? this.sessionConfig : omit(mappings, ['@type']);
		this.mappings = ObjectUtil.extend(this.mappings, config);

		window['AC_CONFIG'] = cloneDeep(this.mappings); // useful for debugging
	}

	/**
	 * You can use ?cc-X=Y to override any config flag.
	 * This is only allowed for internal users in prod DMZ
	 */
	private overrideConfigFromQueryParam(mappings: any) {
		if (UrlUtils.isProdDmz() && (window as any).globalAnalyticsParams?.isInternalLogin !== 'true' && !UrlUtils.isBetaApp()) {
			return;
		}

		const queryParams = new URLSearchParams(location.search);
		for (const qpKey of queryParams.keys()) {
			if (qpKey.startsWith('cc-')) {
				const configKey = qpKey.replace('cc-', '');

				let qpValue: any = queryParams.get(qpKey);
				if (qpValue === 'true' || qpValue === 'false') {
					qpValue = qpValue === 'true';
				} else if (/^[\d\.]+$/.test(qpValue)) {
					qpValue = Number(qpValue);
				}

				set(mappings, configKey, qpValue);
			}
		}
	}

	private setMappings(): void {
		if (!this.localeUtilResolverService) {
			return;
		}

		const locale = this.localeUtilResolverService.getResolvedLocaleValue();
		const configKey = (locale !== this.DEFAULT_COUNTRY) ? `config_${locale}` : 'config';
		this.mappings = this.environment?.[configKey] ?? {};
	}
}
